import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { FileSearch, AlertTriangle, ArrowLeft, Upload, Bot, Download, FileText } from "lucide-react";
import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_LABELS = {
  ai_phrasing: { label: "AI phrasing", color: "var(--rolex-gold)" },
  common_phrase: { label: "Common phrase", color: "#A67C52" },
  style_shift: { label: "Style shift", color: "#8B3A4A" },
  boilerplate: { label: "Boilerplate", color: "#A67C52" },
  uncited_fact: { label: "Uncited fact", color: "var(--error)" },
};

export default function PlagiarismCheckerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [excludeQuotes, setExcludeQuotes] = useState(false);
  const fileRef = useRef(null);

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const charCount = text.length;

  const stripQuotes = (t) => t.replace(/"[^"]*"|'[^']*'|"[^"]*"|'[^']*'/g, "");

  const handleCheck = async () => {
    if (!text.trim()) return;
    setLoading(true); setResult(null);
    const payload = excludeQuotes ? stripQuotes(text.trim()) : text.trim();
    const token = localStorage.getItem("buddy_access_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/plagiarism-check`, { text: payload }, { headers });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("buddy_access_token");
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/plagiarism-upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      if (data.extracted_text) setText(data.extracted_text);
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to process file.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const downloadReport = () => {
    if (!result) return;
    const lines = [
      `Buddy Plagiarism Report`,
      `Generated: ${new Date().toLocaleString()}`,
      ``,
      `Originality Score: ${result.originality_score}/100 (${result.risk_level} risk)`,
      `AI-Content Likelihood: ${result.ai_score}/100 (${result.ai_risk_level} risk)`,
      `Word Count: ${result.word_count}  •  Characters: ${result.character_count}`,
      ``,
      `Summary:`,
      result.summary || "—",
      ``,
      `Flagged Sentences:`,
      ...(result.flags || []).map((f, i) => `${i + 1}. [${f.type}] ${f.sentence}\n   → ${f.reason}`),
      ``,
      `Suggestions:`,
      ...(result.suggestions || []).map((s, i) => `${i + 1}. ${s}`),
      ``,
      `Original Text:`,
      text,
    ].join("\n");
    const blob = new Blob([lines], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `buddy-plagiarism-report-${Date.now()}.txt`; a.click();
  };

  const sc = result?.originality_score ?? 0;
  const ai = result?.ai_score ?? 0;
  const scoreColor = sc >= 80 ? "var(--rolex-green)" : sc >= 60 ? "var(--rolex-gold)" : "var(--error)";
  const aiColor = ai < 40 ? "var(--rolex-green)" : ai < 70 ? "var(--rolex-gold)" : "var(--error)";

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <button onClick={() => navigate("/ai-tools")} className="flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--rolex-green)" }} data-testid="back-to-tools"><ArrowLeft size={14} /> AI Tools</button>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--rolex-green-light)" }}><FileSearch size={20} style={{ color: "var(--rolex-green)" }} /></div>
            <div><h1 className="font-display text-2xl font-bold" style={{ color: "var(--text-primary)" }}>Plagiarism &amp; AI-Content Checker</h1><p className="text-sm" style={{ color: "var(--text-muted)" }}>Originality score · AI-content detection · Per-sentence flags · Downloadable report</p></div>
          </div>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUT */}
          <div className="rounded-2xl p-6 space-y-3" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <input type="file" ref={fileRef} accept=".pdf,.docx,.txt" className="hidden" onChange={handleFileUpload} />
            <button onClick={() => fileRef.current?.click()} className="w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-colors hover:bg-white text-xs font-medium" style={{ borderColor: "var(--surface-border-mid)", color: "var(--text-secondary)" }} data-testid="plag-upload-btn">
              <Upload size={16} style={{ color: "var(--rolex-green)" }} />
              {fileName ? `Uploaded: ${fileName} (replace)` : "Upload PDF, DOCX, or TXT"}
            </button>
            <textarea className="buddy-input resize-none" style={{ minHeight: "200px" }} rows={8} placeholder="Or paste your text here…" value={text} onChange={(e) => { setText(e.target.value); setFileName(""); }} data-testid="plagiarism-input" />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-3">
                <span style={{ color: "var(--text-light)" }} data-testid="plag-word-count">{wordCount} words</span>
                <span style={{ color: "var(--text-light)" }}>{charCount} chars</span>
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer" style={{ color: "var(--text-muted)" }}>
                <input type="checkbox" checked={excludeQuotes} onChange={(e) => setExcludeQuotes(e.target.checked)} className="accent-[var(--rolex-green)]" data-testid="plag-exclude-quotes" />
                Exclude quoted text
              </label>
            </div>
            <motion.button onClick={handleCheck} disabled={!text.trim() || loading} className="btn-primary w-full py-2.5 text-sm" style={{ opacity: !text.trim() ? 0.5 : 1 }} whileHover={{ scale: 1.01 }} data-testid="plagiarism-submit">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto block" /> : "Check Plagiarism & AI Content"}
            </motion.button>
          </div>

          {/* RESULT */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "320px" }}>
            {loading ? (
              <div className="space-y-3 mt-4">
                <div className="h-4 rounded animate-shimmer" style={{ width: "80%" }} />
                <div className="h-4 rounded animate-shimmer" style={{ width: "60%" }} />
                <div className="h-4 rounded animate-shimmer" style={{ width: "70%" }} />
              </div>
            ) : result && !result.error ? (
              <div className="space-y-4">
                {/* Two-score header */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl" style={{ background: "var(--surface-warm)" }} data-testid="plag-originality-card">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full flex flex-col items-center justify-center shrink-0" style={{ border: `3px solid ${scoreColor}` }}>
                        <span className="text-xl font-bold leading-none" style={{ color: scoreColor }} data-testid="plag-originality-score">{sc}</span>
                        <span className="text-[8px]" style={{ color: "var(--text-muted)" }}>/100</span>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Originality</p>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block mt-1" style={{ background: result.risk_level === "low" ? "var(--success-bg)" : result.risk_level === "medium" ? "var(--warning-bg)" : "var(--error-bg)", color: result.risk_level === "low" ? "var(--success)" : result.risk_level === "medium" ? "var(--warning)" : "var(--error)" }}>{result.risk_level?.toUpperCase()} RISK</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: "var(--surface-warm)" }} data-testid="plag-ai-card">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full flex flex-col items-center justify-center shrink-0" style={{ border: `3px solid ${aiColor}` }}>
                        <span className="text-xl font-bold leading-none" style={{ color: aiColor }} data-testid="plag-ai-score">{ai}</span>
                        <span className="text-[8px]" style={{ color: "var(--text-muted)" }}>/100</span>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Bot size={10} /> AI Content</p>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block mt-1" style={{ background: result.ai_risk_level === "low" ? "var(--success-bg)" : result.ai_risk_level === "medium" ? "var(--warning-bg)" : "var(--error-bg)", color: result.ai_risk_level === "low" ? "var(--success)" : result.ai_risk_level === "medium" ? "var(--warning)" : "var(--error)" }}>{result.ai_risk_level?.toUpperCase()} RISK</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid="plag-summary">{result.summary}</p>

                {result.flags?.length > 0 && (
                  <div data-testid="plag-flags">
                    <span className="label-caps block mb-2">Flagged Sentences ({result.flags.length})</span>
                    <div className="space-y-2">
                      {result.flags.map((f, i) => {
                        const meta = ISSUE_LABELS[f.type] || { label: f.type, color: "var(--text-muted)" };
                        return (
                          <div key={i} className="p-2.5 rounded-lg border-l-4" style={{ background: "var(--surface-warm)", borderLeftColor: meta.color }}>
                            <div className="flex items-center gap-1 mb-1">
                              <AlertTriangle size={11} style={{ color: meta.color }} />
                              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                            </div>
                            <p className="text-xs italic mb-1" style={{ color: "var(--text-secondary)" }}>"{f.sentence}"</p>
                            <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{f.reason}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {result.suggestions?.length > 0 && (
                  <div>
                    <span className="label-caps block mb-2">Suggestions</span>
                    <ul className="space-y-1">
                      {result.suggestions.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: "var(--rolex-green)" }} />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-3 flex items-center justify-between" style={{ borderTop: "1px solid var(--surface-border)" }}>
                  <span className="text-[10px]" style={{ color: "var(--text-light)" }}>{result.word_count} words · {result.character_count} chars</span>
                  <button onClick={downloadReport} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }} data-testid="plag-download-btn">
                    <Download size={12} /> Download Report
                  </button>
                </div>
              </div>
            ) : result?.error ? (
              <p className="text-sm p-4 rounded-xl" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{result.message}</p>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <FileText size={32} style={{ color: "var(--surface-border-mid)" }} />
                <p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Results will appear here</p>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-light)" }}>Originality + AI-content detection + per-sentence flags</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
